import axios from 'axios';
import API from "../config/API";
import siteSetting from "../config/env";

export const bookingService = {
    createOnlineBookingV2,
    sendGuestOtp,
    verifyGuestOtp,
    createGuestBooking,
    getBookingConsentContext,
    sendGuestClaimLink,
    getBookingV2,
    createEvent,
    getHairdresserBooking,
    hairdresserCancelBooking,
    createNewCheckoutBooking,
    editCheckoutBooking,
    editProductServiceBooking,
    bookingComplete,
    editDairyBooking,
    noShowBooking,
    userCancelBooking,
    previewCancellationFee,
    previewReschedule,
    clientRescheduleBooking,
    removeBooking,
    checkDateRangeAvailability,
    getAvailableSlots,
    createBookingV2,
    // New v2 client booking APIs
    getClientUpcomingBookings,
    getClientPastBookings,
    updateBookingNotes,
    getBookingByIdV2,
    getBookingSlotsV2,
    resendBookingConfirmation,
    getClientBookingById
};



/**
 * Create an online booking using the v2 API
 * This endpoint properly adds the client to the hairdresser's client list
 * based on data sharing settings
 *
 * @param {Object} params - Booking parameters
 * @param {string} params.hairdresserId - Hairdresser user ID (the stylist being booked)
 * @param {Array} params.servicesToBook - Array of services to book
 * @param {string} params.bookingDate - Booking date (YYYY-MM-DD)
 * @param {Array} params.bookingSlot - Booking time slot [start, end] in minutes from midnight
 * @param {number} params.isPayAtSalonBooking - 1 for pay at salon, 0 for online payment
 * @returns {Promise} API response with booking details
 */
function createOnlineBookingV2(params) {
    let accessToken = params.accessToken || localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    const requestBody = {
        hairdresserId: params.hairdresserId || params.userId,
        servicesToBook: params.servicesToBook,
        bookingDate: params.bookingDate,
        bookingSlot: params.bookingSlot,
        isPayAtSalonBooking: params.isPayAtSalonBooking !== undefined ? params.isPayAtSalonBooking : 1
    };

    // Multi-location: the booking page's site, so the server books the
    // stylist's record THERE (they may work at more than one location).
    if (params.salonId) {
        requestBody.salonId = params.salonId;
    }

    // Acquisition channel → client ownership (client-flows §4a). 'salon_profile'
    // vs 'pro_link'; omitted for older callers, BE falls back to legacy rule.
    if (params.acquisitionChannel) {
        requestBody.acquisitionChannel = params.acquisitionChannel;
    }

    // Tracked booking link (?src=) the client came in on, and when: reporting
    // only (Performance's "where each booking came from").
    if (params.src) {
        requestBody.src = params.src;
        if (params.srcAt) requestBody.srcAt = params.srcAt;
    }

    // Voucher V2 — apply an owned gift card to this booking ({id,code,amount,salonId}).
    // The backend validates ownership/salon/balance and redeems it atomically.
    if (params.giftCard && params.giftCard.code) {
        requestBody.giftCard = params.giftCard;
    }

    // Per-client marketing consent for the sender Rule 1 attributes this
    // booking to: { ownerId?, granted }. The BE re-resolves the owner and
    // records against ITS answer; the FE ownerId is display intent only
    // (may be absent if the consent-context call failed).
    if (params.marketingConsent && typeof params.marketingConsent.granted === 'boolean') {
        requestBody.marketingConsent = params.marketingConsent;
    }

    // Discount code: { code, codeId, amount, lines: [{ index, serviceId,
    // listPrice, discount }] }. Prices in servicesToBook are already after it
    // (listPrice rides along); the server checks and holds the code
    // (docs/DISCOUNT_CODES_BACKEND.md).
    if (params.promo && params.promo.code) {
        requestBody.promo = params.promo;
    }

    console.log('createOnlineBookingV2 params:', requestBody);

    return axios.post(API.CREATE_ONLINE_BOOKING, requestBody, { headers: headers });
}

// Shared header for the unauthenticated guest endpoints (the Basic auth is
// the API gateway credential, not a user session).
const GUEST_HEADERS = {
    'Content-Type': 'application/json',
    'authorization': "Basic YmV1YXBwOmJldWFwcA=="
};

/**
 * Guest checkout step 1 — request an OTP. No auth.
 * @param {Object} params { email, phoneNumber?, channel? }
 * Response responseData.outcome:
 *   'guest'          — code sent to the address they typed.
 *   'account_exists' — email belongs to a claimed account; code sent to the
 *                      account's REGISTERED contact (masked in `sentTo`).
 */
function sendGuestOtp(params) {
    return axios.post(API.GUEST_SEND_OTP, {
        email: params.email,
        phoneNumber: params.phoneNumber,
        channel: params.channel
    }, { headers: GUEST_HEADERS });
}

/**
 * Guest checkout step 2 — verify the OTP. No auth.
 * @param {Object} params { email, code }
 * Response responseData.outcome:
 *   'verified'  — guest may now create the booking (BE consumes the code).
 *   'logged_in' — the email was a claimed account; response carries real
 *                 accessToken / refreshToken / userId / name — continue as a
 *                 logged-in client, not a guest.
 */
function verifyGuestOtp(params) {
    return axios.post(API.GUEST_VERIFY_OTP, {
        email: params.email,
        code: params.code
    }, { headers: GUEST_HEADERS });
}

/**
 * Create a booking as a GUEST (no account). No auth header. Pay-at-salon
 * only — the BE re-validates that regardless of what the FE decided.
 * Creates or reuses an unclaimed client record; returns a booking-scoped
 * manageToken for the success page and the emailed manage link.
 * See GUEST-BOOKING-PLAN.md §4.2.
 *
 * @param {Object} params
 * @param {string} params.hairdresserId
 * @param {Array}  params.servicesToBook   same shape as online-booking
 * @param {string} params.bookingDate      YYYY-MM-DD
 * @param {Array}  params.bookingSlot      [startMins, endMins]
 * @param {Object} params.guest            { firstName, lastName, email, phoneNumber }
 * @param {Object} [params.marketingConsent] { ownerId, granted }
 */
function createGuestBooking(params) {
    const headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    const requestBody = {
        hairdresserId: params.hairdresserId,
        servicesToBook: params.servicesToBook,
        bookingDate: params.bookingDate,
        bookingSlot: params.bookingSlot,
        guest: params.guest,
    };
    // Multi-location: same as createOnlineBookingV2.
    if (params.salonId) {
        requestBody.salonId = params.salonId;
    }
    if (params.src) {
        requestBody.src = params.src;
        if (params.srcAt) requestBody.srcAt = params.srcAt;
    }
    if (params.marketingConsent && typeof params.marketingConsent.granted === 'boolean') {
        requestBody.marketingConsent = params.marketingConsent;
    }
    // Discount code, as createOnlineBookingV2
    if (params.promo && params.promo.code) {
        requestBody.promo = params.promo;
    }
    return axios.post(API.CREATE_GUEST_BOOKING, requestBody, { headers });
}

/**
 * Who would own the client of a prospective booking under Rule 1, and
 * whether to show them a marketing consent checkbox. Works logged-in
 * (token identifies the client) or as a guest (email matches an existing
 * unclaimed record so a returning guest is not re-asked).
 * Resolves to { ownerId, ownerType, ownerName, alreadyConsented, showConsent }.
 * See GUEST-BOOKING-PLAN.md §5A.
 */
/**
 * Guest → account. Sends the existing claim link (set a password) for the
 * guest's unclaimed record. Authenticated by the booking-scoped manageToken
 * so only someone holding the confirmation can trigger it. §4.4.
 */
function sendGuestClaimLink({ bookingId, email, manageToken }) {
    // No `manageToken` header — it isn't CORS-allowed and the BE claim flow
    // authenticates by email anyway. Keep it in the body harmlessly.
    const headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    return axios.post(API.GUEST_SEND_CLAIM_LINK, { bookingId, email, manageToken }, { headers });
}

function getBookingConsentContext({ salonId, hairdresserId, email }) {
    const accessToken = localStorage.getItem('accessToken');
    const headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    if (accessToken) headers.accessToken = accessToken;
    const params = { salonId, hairdresserId };
    if (email) params.email = email;
    return axios.get(API.BOOKING_CONSENT_CONTEXT, { headers, params });
}

function createNewCheckoutBooking(params) {
    //console.log('params',params);
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.CHECKOUT_NEW_BOOKING, params, {headers:headers})
}

function createBookingV2(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.CREATE_BOOKING_V2, params, {headers: headers})
}

function updateBookingNotes(bookingId, bookingNotes) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.put(API.UPDATE_BOOKING_NOTES + bookingId + '/notes', {bookingNote: bookingNotes, bookingNotes: bookingNotes}, {headers: headers})
}

function getBookingByIdV2(bookingId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.get(API.GET_BOOKING_V2 + bookingId, {headers: headers})
}

function getBookingSlotsV2(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    let url = API.BOOKING_SLOTS_V2
        .replace(':salonId', params.salonId)
        .replace(':hairdresserId', params.hairdresserId);
    return axios.get(url, {params: {date: params.date}, headers: headers})
}

function editCheckoutBooking(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.CHECKOUT_EDIT_BOOKING, params, {headers:headers})
}


function editProductServiceBooking(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.CHECKOUT_EDIT_SERVICE_PRODUCT, params, {headers:headers})
}

function bookingComplete(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.BOOKING_COMPLETE, params, {headers:headers})
}


function createEvent(params) {
   let  headers= {
        'Content-Type' : 'application/json',
        'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
    }
    const { userId, ...body } = params;
    return axios.post(siteSetting.api.v2+"hairdresser/"+userId+API.USER_CREATE_EVENT, body, {headers:headers})
}


/**
 * Get booking details by ID using v2 API
 * This is a clean endpoint that doesn't require legacy parameters like deviceToken
 * @param {Object} params - Parameters
 * @param {string} params.bookingId - Booking ID
 * @returns {Promise} API response with booking details
 */
function getBookingV2(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.get(API.GET_BOOKING_BY_ID + params.bookingId, { headers: headers });
}

/**
 * Get booking details for the client who made the booking (confirmation page, client booking detail)
 */
/**
 * Client-facing booking detail. Logged-in clients authenticate with their
 * session. GUESTS pass the booking-scoped `manageToken` (from the guest
 * create response, or the emailed manage link) — sent as `manageToken`
 * header AND query so the BE can pick whichever its middleware prefers.
 * GUEST-BOOKING-PLAN.md §4.3.
 */
function getClientBookingById(bookingId, manageToken = null) {
    // A guest isn't logged in, so their booking-scoped token arrives as
    // `manageToken` — but it IS a real access token (the BE mints a genuine
    // session for the unclaimed record), so it authenticates through the
    // standard `accessToken` header. Send it ONLY as that. Do NOT add a
    // `manageToken` header: it isn't in the server's Access-Control-Allow-
    // Headers, so it fails the CORS preflight (the standard accessToken header
    // is allowed, since every logged-in request uses it).
    // The manage token WINS when present: it is scoped to exactly this booking,
    // whereas a session in localStorage may belong to someone else (a guest
    // opening their email link on a device where another account is logged in,
    // or a stale session) — in which case the BE rightly refuses
    // "you can only view your own bookings".
    let accessToken = manageToken || localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    if (accessToken) headers.accessToken = `${accessToken}`;
    return axios.get(API.GET_CLIENT_BOOKING_BY_ID + bookingId, { headers });
}


function userCancelBooking(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };

    // -----------------------------
    return axios.put(API.CANCEL_BOOKING, params, {headers:headers})
}

function previewReschedule(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(siteSetting.api.v2 + 'booking/previewReschedule', params, { headers });
}

function clientRescheduleBooking(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(siteSetting.api.v2 + 'booking/rescheduleBooking', params, { headers });
}

function previewCancellationFee(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };

    return axios.post(API.PREVIEW_CANCELLATION_FEE, params, {headers:headers})
}


// Staff view of one booking, for the POS invoice (/invoice/:id). Was legacy
// GET hairdresser/:id/bookings/:bookingId; now v2 GET booking/:bookingId
// (salon-scoped, client masked by the sharing rules). The invoice still reads
// the legacy envelope `data.data.bookings[0]` with `user` and `hairdresser`
// populated, so reshape to that. v2 answers HTTP 200 on failure: reject.
function getHairdresserBooking(params) {
    return getBookingByIdV2(params.bookingID || params.bookingId).then((response) => {
        const payload = (response && response.data) || {};
        if (payload.statusCode !== 1 || !payload.responseData) {
            const err = new Error((payload.error && payload.error.responseMessage) || 'Unable to load booking');
            err.response = response;
            throw err;
        }
        const b = payload.responseData;
        const booking = {
            ...b,
            userId: b.clientInfo ? b.clientInfo._id : b.userId,
            user: b.clientInfo || {},
            hairdresserId: b.hairdresserInfo ? b.hairdresserInfo._id : b.hairdresserId,
            hairdresser: b.hairdresserInfo || {},
            // v2 unwinds services, so a products-only sale comes back with one
            // empty line; legacy had none.
            servicesBooked: (b.servicesBooked || []).filter((s) => s && s.servicePrice !== undefined),
        };
        return { ...response, data: { errorCode: 0, data: { bookings: [booking] } } };
    });
}


function hairdresserCancelBooking(params) {
    // v2 salon-cancel (was the legacy v1 hairdresser/:id/bookings/:id/cancel).
    // Same payload shape kept so existing callers don't change; the v2 route
    // only needs bookingId, restores any gift card inline and notifies the
    // client.
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.put(API.SALON_CANCEL_BOOKING, {
        bookingId: params.bookingID || params.bookingId,
        refundDeposit: params.refundDeposit ? 1 : 0,
    }, {headers:headers})
}


function editDairyBooking(params) {

    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.EDIT_DAIRY_BOOKING, params, {headers:headers})
}


function checkDateRangeAvailability(params) {
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    
    console.log("checkDateRangeAvailability params:", params);
    console.log("Parameter types:", {
        salonId: typeof params.salonId,
        hairdresserId: typeof params.hairdresserId,
        startDate: typeof params.startDate,
        endDate: typeof params.endDate
    });
    
    // Check if salonId and hairdresserId are valid
    if (!params.salonId || !params.hairdresserId) {
        console.error("Missing required parameters:", {
            salonId: params.salonId,
            hairdresserId: params.hairdresserId
        });
    }
    
    // Ensure we're using the correct hairdresser ID format
    // MongoDB IDs are typically 24-character hex strings
    if (params.hairdresserId && params.hairdresserId.length !== 24) {
        console.warn("hairdresserId may not be in the correct format:", params.hairdresserId);
    }
    
    // Replace URL placeholders with actual values
    let url = API.DATE_RANGE_AVAILABILITY
        .replace(':salonId', params.salonId || 'missing-salon-id')
        .replace(':hairdresserId', params.hairdresserId || 'missing-hairdresser-id');
    
    console.log("API URL after replacement:", url);
    
    // Send remaining parameters in the request body
    const requestBody = {
        startDate: params.startDate,
        endDate: params.endDate,
        serviceTime: params.serviceTime // Add service time to filter slots
    };
    
    console.log("Request body:", requestBody);
    
    return axios.post(url, requestBody, { headers: headers })
        .then(response => {
            console.log("API response status:", response.status);
            return response;
        })
        .catch(error => {
            console.error("API error:", error.message);
            if (error.response) {
                console.error("Error response:", error.response.data);
            }
            throw error;
        });
}

function getAvailableSlots(params) {
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    
    console.log("getAvailableSlots params:", params);
    console.log("Parameter types:", {
        salonId: typeof params.salonId,
        hairdresserId: typeof params.hairdresserId,
        date: typeof params.date,
        serviceTime: typeof params.serviceTime
    });
    
    // Check if salonId and hairdresserId are valid
    if (!params.salonId || !params.hairdresserId) {
        console.error("Missing required parameters:", {
            salonId: params.salonId,
            hairdresserId: params.hairdresserId
        });
    }
    
    // Ensure we're using the correct hairdresser ID format
    // MongoDB IDs are typically 24-character hex strings
    if (params.hairdresserId && params.hairdresserId.length !== 24) {
        console.warn("hairdresserId may not be in the correct format:", params.hairdresserId);
    }
    
    // Replace URL placeholders with actual values
    let url = API.AVAILABLE_SLOTS
        .replace(':salonId', params.salonId || 'missing-salon-id')
        .replace(':hairdresserId', params.hairdresserId || 'missing-hairdresser-id');
    
    console.log("API URL after replacement:", url);
    
    // Send remaining parameters in the query string
    const queryParams = {
        date: params.date,
        serviceTime: params.serviceTime, // Add service time to filter slots
        // Which services — lets the BE drop slots no required room can host
        ...(params.serviceIds ? { serviceIds: params.serviceIds } : {})
    };
    
    console.log("Query params:", queryParams);
    
    return axios.get(url, { params: queryParams, headers: headers })
        .then(response => {
            console.log("API response status:", response.status);
            return response;
        })
        .catch(error => {
            console.error("API error:", error.message);
            if (error.response) {
                console.error("Error response:", error.response.data);
            }
            throw error;
        });
}

function noShowBooking(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.put(API.NO_SHOW_BOOKING,params, {headers:headers})
}

function removeBooking(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.put(API.REMOVE_BOOKING,params, {headers:headers})
}

/**
 * Get client's upcoming bookings (v2 API)
 * @param {Object} params - Query parameters
 * @param {number} params.skip - Number of records to skip (default: 0)
 * @param {number} params.limit - Number of records to return (default: 20)
 * @returns {Promise} API response with bookings and pagination
 */
function getClientUpcomingBookings(params = {}) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 20
    };
    
    return axios.get(API.CLIENT_UPCOMING_BOOKINGS, { params: queryParams, headers: headers });
}

/**
 * Get client's past bookings (v2 API)
 * @param {Object} params - Query parameters
 * @param {number} params.skip - Number of records to skip (default: 0)
 * @param {number} params.limit - Number of records to return (default: 20)
 * @returns {Promise} API response with bookings and pagination
 */
function getClientPastBookings(params = {}) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    const queryParams = {
        skip: params.skip || 0,
        limit: params.limit || 20
    };
    
    return axios.get(API.CLIENT_PAST_BOOKINGS, { params: queryParams, headers: headers });
}

function resendBookingConfirmation(bookingId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.RESEND_BOOKING_CONFIRMATION, { bookingId }, { headers: headers });
}